import { graphicEditorEffectiveCp } from './runningWorkoutVizShared';
import { blendSegmentBpmWithRaceProfile } from './segmentSustainedRaceHrEstimate';

/** Fallback pace (sec/km) when no threshold is cached (~6:00/km). */
const DEFAULT_THRESHOLD_PACE_SEC_PER_KM = 360;

function predictPaceSecPerKmFromWatts(model, watts) {
  if (!model || !Number.isFinite(watts) || watts <= 0) return null;
  if (model.type === 'anchored') {
    const pace = model.anchorPaceSecPerKm + model.b * (watts - model.cpWatts);
    if (!Number.isFinite(pace)) return null;
    return Math.min(900, Math.max(150, pace));
  }
  const pace = model.a + model.b * watts;
  if (!Number.isFinite(pace)) return null;
  return Math.min(900, Math.max(150, pace));
}

function predictHrFromWatts(model, watts) {
  if (!model || !Number.isFinite(watts) || watts <= 0) return null;
  const hr = model.a + model.b * watts;
  if (!Number.isFinite(hr)) return null;
  return Math.min(220, Math.max(80, Math.round(hr)));
}

/** Anchor stream/lap HR slope at weekly threshold BPM at CP (matches baseline footnote). */
function predictHrFromWattsAnchored(model, watts, cpWatts, thresholdBpm) {
  if (!model || !Number.isFinite(watts) || watts <= 0) return null;
  const b = Number(model.b);
  if (
    thresholdBpm != null &&
    thresholdBpm > 0 &&
    cpWatts != null &&
    cpWatts > 0 &&
    Number.isFinite(b) &&
    b > 0
  ) {
    const hr = thresholdBpm + b * (watts - cpWatts);
    if (!Number.isFinite(hr)) return null;
    return Math.min(220, Math.max(80, Math.round(hr)));
  }
  return predictHrFromWatts(model, watts);
}

/** HR from weekly LTHR × %CP — 100 % CP → threshold BPM; matches moderate aerobic segments well. */
function thresholdCurveBpmForCp(thresholdBpm, cp) {
  if (thresholdBpm == null || thresholdBpm <= 0 || cp <= 0) return null;
  const hr = thresholdBpm * (0.55 + 0.45 * (cp / 100));
  return Math.min(220, Math.max(80, Math.round(hr)));
}

/** @param {object} seg @param {number|null} thresholdBpm @param {{ cpWatts?, powerHrModel?, durationSec?, raceHrProfile? }} [opts] */
export function estimateBpmForSegment(seg, thresholdBpm, opts = {}) {
  const { cpWatts, powerHrModel, durationSec, raceHrProfile } = opts;
  const cp = segmentMidpointCpPercent(seg);
  const ic = (seg.run_intensity_class || 'work').toLowerCase();
  let baseBpm = null;

  if (powerHrModel && cpWatts != null && cpWatts > 0 && cp > 0) {
    const watts = (cpWatts * cp) / 100;
    baseBpm = predictHrFromWattsAnchored(powerHrModel, watts, cpWatts, thresholdBpm);
  }

  const curveBpm = thresholdCurveBpmForCp(thresholdBpm, cp);
  if (baseBpm != null && curveBpm != null && ic === 'work') {
    // Stream-anchored slope can undershoot at aerobic-moderate %CP; keep the higher estimate.
    baseBpm = Math.max(baseBpm, curveBpm);
  } else if (baseBpm == null && curveBpm != null) {
    baseBpm = curveBpm;
  }

  if (baseBpm == null) return null;
  return blendSegmentBpmWithRaceProfile(baseBpm, seg, thresholdBpm, durationSec, raceHrProfile);
}

/** Midpoint %CP when a range is set (matches table watts). */
export function segmentMidpointCpPercent(seg) {
  const lo = seg.cp_percent_min === '' || seg.cp_percent_min == null ? NaN : Number(seg.cp_percent_min);
  const hi = seg.cp_percent_max === '' || seg.cp_percent_max == null ? NaN : Number(seg.cp_percent_max);
  if (Number.isFinite(lo) && Number.isFinite(hi)) return (lo + hi) / 2;
  return graphicEditorEffectiveCp(seg) || 0;
}

export const METERS_PER_MILE = 1609.344;

/**
 * @param {number} sec
 * @param {(k: string, p?: object) => string} t
 */
export function formatSplitDurationLabel(sec, t) {
  const s = Math.round(Number(sec) || 0);
  if (s <= 0) return t('runningExercise.graphicNoDuration');
  if (s <= 90) return t('runningExercise.graphicSeconds', { n: s });
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

/** Edit field: always m:ss (includes 0:45 for sub-minute). */
export function formatSecToMss(sec) {
  const s = Math.round(Number(sec) || 0);
  if (s < 0) return '';
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

/**
 * Parse duration from "m:ss", "mm:ss", or plain seconds.
 * @returns {number|null} seconds or null if invalid / empty
 */
export function parseMssToSec(str) {
  const raw = String(str ?? '').trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n >= 0 ? n : null;
  }
  const parts = raw.split(':').map((p) => p.trim());
  if (parts.length === 2) {
    const m = parseInt(parts[0], 10);
    const sec = parseInt(parts[1], 10);
    if (!Number.isFinite(m) || !Number.isFinite(sec) || sec < 0 || sec > 59 || m < 0) return null;
    return m * 60 + sec;
  }
  if (parts.length === 3) {
    const h = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const sec = parseInt(parts[2], 10);
    if (
      !Number.isFinite(h) ||
      !Number.isFinite(m) ||
      !Number.isFinite(sec) ||
      h < 0 ||
      m < 0 ||
      m > 59 ||
      sec < 0 ||
      sec > 59
    ) {
      return null;
    }
    return (h * 60 + m) * 60 + sec;
  }
  return null;
}

/** @param {'km'|'miles'} unit */
export function metersToDistInputStr(meters, unit) {
  const m = Number(meters);
  if (!Number.isFinite(m) || m <= 0) return '';
  const v = unit === 'miles' ? m / METERS_PER_MILE : m / 1000;
  const rounded = unit === 'miles' ? Math.round(v * 1000) / 1000 : Math.round(v * 1000) / 1000;
  return String(rounded);
}

/** @param {'km'|'miles'} unit */
export function distInputStrToMeters(str, unit) {
  const v = parseFloat(String(str ?? '').replace(',', '.'));
  if (!Number.isFinite(v) || v <= 0) return null;
  return Math.round(unit === 'miles' ? v * METERS_PER_MILE : v * 1000);
}

/**
 * Estimated time to cover segment distance at estimated pace.
 * @returns {number|null} seconds
 */
export function estimatedDurationSecFromDistance(seg, thresholdPaceSecPerKm, paceEstimateOpts = {}) {
  const dm = seg.distance_m === '' || seg.distance_m == null ? NaN : Number(seg.distance_m);
  if (!Number.isFinite(dm) || dm <= 0) return null;
  const pace = estimatePaceSecPerKm(seg, thresholdPaceSecPerKm, paceEstimateOpts);
  return Math.round((dm / 1000) * pace);
}

/**
 * Pace (sec/km) from watts–pace calibration when available; else threshold × %CP.
 * @param {object} seg — row with run_intensity_class, cp_percent
 * @param {number|null|undefined} thresholdPaceSecPerKm
 * @param {{ cpWatts?: number|null, powerPaceModel?: { a: number, b: number }|null }} [opts]
 */
export function estimatePaceSecPerKm(seg, thresholdPaceSecPerKm, opts = {}) {
  const { cpWatts, powerPaceModel } = opts;
  const ic = (seg.run_intensity_class || 'work').toLowerCase();
  const cp = segmentMidpointCpPercent(seg);

  const base =
    thresholdPaceSecPerKm != null && thresholdPaceSecPerKm > 0
      ? thresholdPaceSecPerKm
      : DEFAULT_THRESHOLD_PACE_SEC_PER_KM;

  if (powerPaceModel && cpWatts != null && cpWatts > 0 && cp > 0) {
    const watts = (cpWatts * cp) / 100;
    const fromPower = predictPaceSecPerKmFromWatts(powerPaceModel, watts);
    if (fromPower != null) {
      if (ic === 'rest') return fromPower * 1.08;
      return fromPower;
    }
  }
  if (ic === 'rest') return base * 1.45;
  if (cp <= 0) return base * 1.22;
  return base * (100 / Math.max(45, Math.min(150, cp)));
}

/**
 * Meters to show on the distance footer line (stored distance or duration × estimated pace).
 */
export function metersForSegmentDistanceLine(seg, thresholdPaceSecPerKm, paceEstimateOpts = {}) {
  const mode = (seg.split_measure_mode || 'duration').toLowerCase() === 'distance' ? 'distance' : 'duration';
  const dur = seg.duration_sec === '' || seg.duration_sec == null ? 0 : Number(seg.duration_sec) || 0;
  const dm = seg.distance_m === '' || seg.distance_m == null ? 0 : Number(seg.distance_m) || 0;
  if (mode === 'distance' && dm > 0) return dm;
  if (mode === 'duration' && dur > 0) {
    const pace = estimatePaceSecPerKm(seg, thresholdPaceSecPerKm, paceEstimateOpts);
    return (dur / pace) * 1000;
  }
  if (dm > 0) return dm;
  return null;
}

/**
 * @param {number} meters
 * @param {'km'|'miles'} distanceUnits
 * @param {(k: string, p?: object) => string} t
 */
/**
 * @returns {'km'|'miles'|null}
 */
export function normalizeDistanceDisplayUnit(u) {
  if (u == null || u === '') return null;
  const s = String(u).toLowerCase().trim();
  if (s === 'miles' || s === 'mile' || s === 'mi') return 'miles';
  if (s === 'km' || s === 'kilometer' || s === 'kilometre') return 'km';
  return null;
}

/**
 * @param {'km'|'miles'} userDefaultKmOrMiles — from Settings when segment has no stored unit
 */
export function effectiveDistanceDisplayUnit(seg, userDefaultKmOrMiles) {
  const n = normalizeDistanceDisplayUnit(seg.distance_display_unit);
  if (n) return n;
  return userDefaultKmOrMiles === 'miles' ? 'miles' : 'km';
}

/**
 * Snap horizontal-resize distance to editor steps: Shift → 1 km or 1 mi; else 0.5, or 0.1 when &lt; 0.5 in display units.
 * @param {'km'|'miles'} displayUnit
 */
export function snapDistanceMeters(meters, displayUnit, shiftSnap) {
  const unit = displayUnit === 'miles' ? 'miles' : 'km';
  const v = unit === 'miles' ? meters / METERS_PER_MILE : meters / 1000;
  if (!Number.isFinite(v) || v <= 0) {
    const fallback = Math.round(meters || 0);
    return Math.max(unit === 'miles' ? Math.round(METERS_PER_MILE) : 1000, fallback);
  }
  const step = shiftSnap ? 1 : v < 0.5 ? 0.1 : 0.5;
  let snappedV = Math.round(v / step) * step;
  if (snappedV < step) snappedV = step;
  const out = unit === 'miles' ? Math.round(snappedV * METERS_PER_MILE) : Math.round(snappedV * 1000);
  const minM = unit === 'miles' ? Math.round(METERS_PER_MILE) : 1000;
  return Math.max(minM, out);
}

/**
 * Convert every distance-primary split to the other unit, rounding to the nearest whole km or whole mi.
 * @param {'km'|'miles'} userDefaultKmOrMiles
 */
export function flipAllDistanceSegmentUnits(segments, userDefaultKmOrMiles) {
  return segments.map((row) => {
    if (String(row.split_measure_mode || '').toLowerCase() !== 'distance') return row;
    const dm = Number(row.distance_m);
    if (!Number.isFinite(dm) || dm <= 0) return row;
    const u = effectiveDistanceDisplayUnit(row, userDefaultKmOrMiles);
    if (u === 'miles') {
      const km = dm / 1000;
      const kmInt = Math.max(1, Math.round(km));
      return { ...row, distance_m: kmInt * 1000, distance_display_unit: 'km' };
    }
    const mi = dm / METERS_PER_MILE;
    const miInt = Math.max(1, Math.round(mi));
    return { ...row, distance_m: Math.round(miInt * METERS_PER_MILE), distance_display_unit: 'miles' };
  });
}

export function formatDistanceFromMeters(meters, distanceUnits, t) {
  if (meters == null || !Number.isFinite(meters) || meters <= 0) return null;
  if (distanceUnits === 'miles') {
    const mi = meters / METERS_PER_MILE;
    const n = mi >= 10 ? mi.toFixed(1) : mi.toFixed(2);
    return t('runningExercise.graphicDistanceMi', { n });
  }
  const km = meters / 1000;
  const n = km >= 10 ? km.toFixed(1) : km.toFixed(2);
  return t('runningExercise.graphicDistanceKm', { n });
}
